import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { useCanonical } from "@/lib/useCanonical";
import { track } from "@/lib/analytics";
import {
  ArrowRight,
  Gift,
  Check,
  Star,
  Clock,
  Video,
  Sparkles,
  MessageCircle,
  Volume2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  pt: {
    badge: "1ª aula · 100% grátis · sem cartão",
    // Three headline variants for A/B/C testing.
    // Variant A: emphasises the human ("parisiense de verdade")
    // Variant B: emphasises the outcome ("falar em 30 dias")
    // Variant C: emphasises social proof (numbers of students)
    titleA1: "Sua primeira aula de francês",
    titleA2: "com um parisiense de verdade — grátis.",
    titleB1: "Comece a falar francês em 30 dias",
    titleB2: "com um professor nativo de Paris — grátis.",
    titleC1: "500+ brasileiros já destravaram o francês",
    titleC2: "com o método do Prof Lionel — sua vez, grátis.",
    sub: "50 minutos, cara a cara, no Zoom. Você fala, eu corrijo, a gente traça teu plano. Resposta em até 24 horas.",
    nameLabel: "Como devo te chamar?",
    namePh: "Mariana",
    phoneLabel: "Seu WhatsApp",
    phonePh: "(21) 9 9999-9999",
    submit: "Quero minha aula grátis",
    submitting: "Enviando…",
    micro: "Sem cartão. Sem trote comercial. Você escolhe o horário depois que eu te respondo.",
    success: "Recebi, {name}! Te chamo no WhatsApp em até 24 h.",
    errName: "Coloca seu nome, por favor.",
    errPhone: "Coloca um WhatsApp válido.",
    errGeneric: "Não deu certo. Tenta novamente?",
    proofTitle: "O que meus alunos dizem",
    audioProofTitle: "Ouça o Prof Lionel — em 20 segundos",
    audioProofSubtitle: "Antes de reservar, escuta a voz que vai te ensinar francês. Sotaque parisiense de verdade, sem filtro.",
    proofs: [
      { name: "Marina, 34 · Rio", text: "Em 3 meses saí do « bonjour » travado pra apresentar meu projeto em francês numa reunião. Prof Lionel é paciente e certeiro." },
      { name: "Pedro, 41 · São Paulo", text: "Passei no DELF B2 com 78/100 depois de 6 meses de aulas. O método é diferente: nada de decorar, tudo prático." },
      { name: "Aline, 28 · BH", text: "Fui pra Paris em janeiro. Chegou o dia e falei sem pânico. Valeu cada aula, valeu o método « M. Bonjour-Merci »." },
    ],
    bullets: [
      { Icon: Video, text: "50 min ao vivo no Zoom" },
      { Icon: Star, text: "20 anos ensinando brasileiros" },
      { Icon: Clock, text: "Resposta em 24h" },
      { Icon: Gift, text: "Zero cartão, zero pressão" },
    ],
    footer: "© 2026 Na Ponta Da Língua Brasil · Prof Lionel Philippe",
  },
  en: {
    badge: "1st class · 100% free · no card",
    titleA1: "Your first French class",
    titleA2: "with a real Parisian — free.",
    titleB1: "Start speaking French in 30 days",
    titleB2: "with a native Parisian teacher — free.",
    titleC1: "500+ Brazilians unlocked their French",
    titleC2: "with Prof Lionel's method — your turn, free.",
    sub: "50 minutes, face to face, on Zoom. You speak, I correct, we build your plan. Reply within 24 hours.",
    nameLabel: "What should I call you?",
    namePh: "Mariana",
    phoneLabel: "Your WhatsApp",
    phonePh: "+55 21 9 9999-9999",
    submit: "Book my free class",
    submitting: "Sending…",
    micro: "No card. No sales pressure. You pick the slot after I get back to you.",
    success: "Got it, {name}! I'll message you on WhatsApp within 24 h.",
    errName: "Please tell me your name.",
    errPhone: "Please add a valid WhatsApp.",
    errGeneric: "Something failed. Try again?",
    proofTitle: "What my students say",
    audioProofTitle: "Hear Prof Lionel — in 20 seconds",
    audioProofSubtitle: "Before booking, listen to the voice that'll teach you. Real Parisian accent, no filter.",
    proofs: [
      { name: "Marina, 34 · Rio", text: "In 3 months I went from freezing at « bonjour » to presenting my project in French at a meeting. Prof Lionel is patient and precise." },
      { name: "Pedro, 41 · São Paulo", text: "Passed DELF B2 with 78/100 after 6 months. The method is different: no memorising, everything practical." },
      { name: "Aline, 28 · BH", text: "Flew to Paris in January. When the day came I spoke without panic. Worth every class." },
    ],
    bullets: [
      { Icon: Video, text: "50 min live on Zoom" },
      { Icon: Star, text: "20 years teaching Brazilians" },
      { Icon: Clock, text: "Reply within 24h" },
      { Icon: Gift, text: "Zero card, zero pressure" },
    ],
    footer: "© 2026 Na Ponta Da Língua Brasil · Prof Lionel Philippe",
  },
  fr: {
    badge: "1er cours · 100 % gratuit · sans carte",
    titleA1: "Ton premier cours de français",
    titleA2: "avec un vrai parisien — gratuit.",
    titleB1: "Commence à parler français en 30 jours",
    titleB2: "avec un prof natif de Paris — gratuit.",
    titleC1: "500 Brésiliens ont déjà débloqué leur français",
    titleC2: "avec la méthode du Prof Lionel — ton tour, gratuit.",
    sub: "50 minutes, face à face, sur Zoom. Tu parles, je corrige, on trace ton plan. Réponse sous 24 heures.",
    nameLabel: "Comment t'appeler ?",
    namePh: "Mariana",
    phoneLabel: "Ton WhatsApp",
    phonePh: "+55 21 9 9999-9999",
    submit: "Je réserve mon cours gratuit",
    submitting: "Envoi…",
    micro: "Sans carte. Sans pression. Tu choisis le créneau après ma réponse.",
    success: "C'est bon, {name} ! Je t'écris sur WhatsApp sous 24 h.",
    errName: "Dis-moi comment t'appeler.",
    errPhone: "WhatsApp invalide.",
    errGeneric: "Ça n'a pas marché. On réessaie ?",
    proofTitle: "Ce que mes élèves disent",
    audioProofTitle: "Écoute le Prof Lionel — en 20 secondes",
    audioProofSubtitle: "Avant de réserver, écoute la voix qui va t'enseigner. Accent parisien véritable, sans filtre.",
    proofs: [
      { name: "Marina, 34 · Rio", text: "En 3 mois je suis passée du « bonjour » figé à présenter mon projet en français en réunion. Patient et précis." },
      { name: "Pedro, 41 · São Paulo", text: "DELF B2 réussi avec 78/100 après 6 mois. Méthode différente : pas de par cœur, tout pratique." },
      { name: "Aline, 28 · BH", text: "Partie à Paris en janvier. J'ai parlé sans paniquer. Chaque cours a valu le coup." },
    ],
    bullets: [
      { Icon: Video, text: "50 min en direct sur Zoom" },
      { Icon: Star, text: "20 ans avec des Brésiliens" },
      { Icon: Clock, text: "Réponse sous 24h" },
      { Icon: Gift, text: "Zéro carte, zéro pression" },
    ],
    footer: "© 2026 Na Ponta Da Língua Brasil · Prof Lionel Philippe",
  },
};

const AulaGratis = () => {
  const { lang } = useLanguage();
  const t = COPY[lang] || COPY.pt;
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Deterministic 50/50 A/B assignment, sticky per browser via localStorage.
  // Reads once on first render — hydration-safe because assigns "a" default
  // and switches in useEffect (which runs post-hydration).
  const [variant, setVariant] = useState("a");

  useCanonical("/aula-gratis");

  useEffect(() => {
    let v;
    try {
      v = localStorage.getItem("ab_aula_gratis");
      if (!v || (v !== "a" && v !== "b" && v !== "c")) {
        // 3-way random split; also supports UTM override (`?v=a|b|c`) for
        // deterministic testing of a specific variant from an ad.
        const forced = new URLSearchParams(window.location.search).get("v");
        if (forced && ["a", "b", "c"].includes(forced)) {
          v = forced;
        } else {
          const r = Math.random();
          v = r < 1 / 3 ? "a" : r < 2 / 3 ? "b" : "c";
        }
        localStorage.setItem("ab_aula_gratis", v);
      }
    } catch {
      v = "a";
    }
    setVariant(v);
    document.title =
      lang === "fr"
        ? "1er cours de français GRATUIT · Prof Lionel"
        : lang === "en"
        ? "1st free French class · Prof Lionel"
        : "1ª aula de francês GRÁTIS · Prof Lionel";
    track("landing_view", {
      variant: "aula_gratis",
      ab_variant: v,
      language: lang,
    });
  }, [lang]);

  // BreadcrumbList + Offer + Speakable JSON-LD for the free-class LP.
  useEffect(() => {
    const url = "https://npdlbr.com.br/aula-gratis";
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Início", item: "https://npdlbr.com.br/" },
        {
          "@type": "ListItem",
          position: 2,
          name:
            lang === "fr"
              ? "1er cours de français gratuit"
              : lang === "en"
              ? "1st free French class"
              : "1ª aula de francês grátis",
          item: url,
        },
      ],
    };
    const offer = {
      "@context": "https://schema.org",
      "@type": "Offer",
      "@id": `${url}#offer`,
      url,
      name:
        lang === "fr"
          ? "1er cours de français en ligne offert"
          : lang === "en"
          ? "First online French class free"
          : "1ª aula de francês online grátis",
      description:
        lang === "fr"
          ? "45-60 min avec Prof Lionel, natif de Paris. Sans CB, sans engagement."
          : lang === "en"
          ? "45-60 min with Prof Lionel, native Parisian. No card, no commitment."
          : "45-60 min com Prof Lionel, nativo de Paris. Sem cartão, sem compromisso.",
      price: "0",
      priceCurrency: "BRL",
      availability: "https://schema.org/InStock",
      category: "Language course",
      itemOffered: { "@type": "Service", name: "Aula particular de francês online" },
      seller: {
        "@type": "EducationalOrganization",
        name: "Na Ponta Da Língua Brasil",
        url: "https://npdlbr.com.br",
      },
    };
    const webPage = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": url,
      url,
      inLanguage: lang,
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [".ag-hero-title", ".ag-hero-subtitle"],
      },
    };
    const emit = (obj, tag) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.dataset.dynamic = tag;
      el.textContent = JSON.stringify(obj);
      document.head.appendChild(el);
      return el;
    };
    const els = [
      emit(breadcrumb, "ag-breadcrumb"),
      emit(offer, "ag-offer"),
      emit(webPage, "ag-webpage"),
    ];
    return () => els.forEach((el) => el.remove());
  }, [lang]);

  const title1 =
    variant === "c" ? t.titleC1 : variant === "b" ? t.titleB1 : t.titleA1;
  const title2 =
    variant === "c" ? t.titleC2 : variant === "b" ? t.titleB2 : t.titleA2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error(t.errName);
      return;
    }
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 8) {
      toast.error(t.errPhone);
      return;
    }
    setLoading(true);
    try {
      // Email is optional in /api/leads but required by schema. Compose a stub.
      const stubEmail = `whatsapp+${digits}@lead.npdlbr.com.br`;
      await axios.post(`${API}/leads`, {
        name: trimmed,
        email: stubEmail,
        phone: phone.trim(),
        language: lang,
        source: `aula_gratis_lp_${variant}`,
        website,
      });
      track("lead_submit_success", {
        language: lang,
        source: "aula_gratis_lp",
        ab_variant: variant,
        has_phone: true,
      });
      const first = trimmed.split(" ")[0];
      toast.success(t.success.replace("{name}", first));
      setDone(true);
      setName("");
      setPhone("");
    } catch (err) {
      track("lead_submit_error", { language: lang, source: "aula_gratis_lp" });
      toast.error(t.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="aula-gratis-page"
      className="min-h-screen bg-[#f4f1ea] text-[#1e2a5a]"
    >
      <main className="max-w-2xl mx-auto px-6 sm:px-10 py-14 sm:py-20">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 rounded-full bg-[#d9381e]/10 border border-[#d9381e]/25 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-[#d9381e] font-semibold">
          <Gift className="h-3 w-3" strokeWidth={2.5} />
          {t.badge}
        </div>

        {/* Headline */}
        <h1
          data-testid="aula-gratis-title"
          data-ab-variant={variant}
          className="ag-hero-title font-display mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
        >
          {title1}{" "}
          <em className="italic font-medium text-[#d9381e]">{title2}</em>
        </h1>

        <p className="ag-hero-subtitle mt-6 text-base sm:text-lg text-[#3a3a3a] leading-relaxed max-w-xl">
          {t.sub}
        </p>

        {/* Trust bullets */}
        <ul className="mt-8 grid grid-cols-2 gap-3">
          {t.bullets.map(({ Icon, text }, i) => (
            <li
              key={i}
              className="flex items-center gap-2 text-xs sm:text-sm text-[#1e2a5a] bg-white/60 border border-[#e5e3db] rounded-lg px-3 py-2"
            >
              <Icon className="h-4 w-4 text-[#d9381e] shrink-0" strokeWidth={1.75} />
              <span>{text}</span>
            </li>
          ))}
        </ul>

        {/* Form */}
        {!done ? (
          <form
            onSubmit={handleSubmit}
            data-testid="aula-gratis-form"
            className="mt-10 bg-white border border-[#1e2a5a]/15 rounded-2xl p-6 sm:p-8 shadow-[0_24px_60px_-30px_rgba(30,42,90,0.3)]"
          >
            {/* honeypot */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="hidden"
              aria-hidden="true"
            />

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="lp-name"
                  className="block text-xs uppercase tracking-[0.18em] text-[#5c5c5c] mb-1.5"
                >
                  {t.nameLabel}
                </label>
                <input
                  id="lp-name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t.namePh}
                  data-testid="aula-gratis-name-input"
                  className="w-full rounded-xl border border-[#e5e3db] px-4 py-3 text-base focus:border-[#d9381e] focus:outline-none focus:ring-2 focus:ring-[#d9381e]/15 transition-colors"
                />
              </div>

              <div>
                <label
                  htmlFor="lp-phone"
                  className="block text-xs uppercase tracking-[0.18em] text-[#5c5c5c] mb-1.5"
                >
                  <MessageCircle className="inline h-3 w-3 -mt-0.5 mr-1" />
                  {t.phoneLabel}
                </label>
                <input
                  id="lp-phone"
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t.phonePh}
                  data-testid="aula-gratis-phone-input"
                  className="w-full rounded-xl border border-[#e5e3db] px-4 py-3 text-base focus:border-[#d9381e] focus:outline-none focus:ring-2 focus:ring-[#d9381e]/15 transition-colors"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="aula-gratis-submit"
              className="group mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#d9381e] px-6 py-4 text-base font-semibold text-[#f4f1ea] transition-colors hover:bg-[#1e2a5a] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? t.submitting : t.submit}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>

            <p className="mt-4 text-xs text-[#5c5c5c] leading-relaxed text-center">
              {t.micro}
            </p>
          </form>
        ) : (
          <div
            data-testid="aula-gratis-success"
            className="mt-10 bg-[#e8f4ea] border border-[#1e7a3e]/25 rounded-2xl p-8 text-center"
          >
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#1e7a3e] mb-4">
              <Check className="h-7 w-7 text-white" strokeWidth={3} />
            </div>
            <p className="font-display text-2xl text-[#1e2a5a]">
              {t.success.replace("{name}", "amigo(a)")}
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 mt-6 text-sm link-underline"
            >
              <Sparkles className="h-4 w-4 text-[#d9381e]" />
              Enquanto isso, lê um post no blog
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}

        {/* Social proof */}
        <section className="mt-16">
          <p className="overline">{t.proofTitle}</p>

          {/* Audio testimonial — listen to Prof Lionel before booking */}
          <div
            className="mt-6 rounded-xl border border-[#1e2a5a] bg-[#1e2a5a] text-[#f4f1ea] p-5 sm:p-6"
            data-testid="audio-proof-block"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#d9381e] flex items-center justify-center">
                <Volume2 className="h-5 w-5 text-[#f4f1ea]" strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-[#e5b3b3]">
                  🎙️ {lang === "fr" ? "Voix du prof" : lang === "en" ? "Prof's voice" : "A voz do prof"}
                </p>
                <p className="mt-2 font-display text-lg sm:text-xl text-[#f4f1ea] leading-tight">
                  {t.audioProofTitle}
                </p>
                <p className="mt-2 text-sm text-[#f4f1ea]/85 leading-relaxed">
                  {t.audioProofSubtitle}
                </p>
                <audio
                  controls
                  preload="metadata"
                  src="/audio/intro-lionel.mp3"
                  className="mt-4 w-full"
                  data-testid="audio-proof-player"
                  onPlay={() =>
                    track("aula_gratis_audio_play", { language: lang, variant })
                  }
                >
                  {lang === "fr"
                    ? "Ton navigateur ne supporte pas l'audio."
                    : lang === "en"
                    ? "Your browser doesn't support audio."
                    : "Seu navegador não suporta áudio."}
                </audio>
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-4">
            {t.proofs.map((p, i) => (
              <blockquote
                key={i}
                className="bg-white border border-[#e5e3db] rounded-xl p-5"
              >
                <div className="flex text-[#d9381e] mb-2">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="h-3.5 w-3.5 fill-current" />
                  ))}
                </div>
                <p className="text-sm text-[#3a3a3a] leading-relaxed italic">
                  « {p.text} »
                </p>
                <p className="mt-3 text-xs uppercase tracking-[0.18em] text-[#5c5c5c]">
                  {p.name}
                </p>
              </blockquote>
            ))}
          </div>
        </section>

        <p className="mt-16 text-xs text-[#5c5c5c] text-center">
          {t.footer} ·{" "}
          <Link to="/" className="link-underline">
            npdlbr.com.br
          </Link>
        </p>
      </main>
    </div>
  );
};

export default AulaGratis;
